// Frontend/src/matic/hooks/useAuthorization.ts
'use client';

import {useAuth} from './useAuth';
import {useAppContext} from '@/matic/providers/AppProvider';
import * as documentPermissions from '@/matic/features/vault/documents/authz/permissions';
import * as revisionPermissions from '@/matic/features/vault/revisions/authz/permissions';
import * as trainingPermissions from '@/matic/features/training/programs/authz/permissions';
import * as latticePermissions from '@/matic/features/lattice/authz/permissions';
import {type AuthUser} from '@/matic/types/auth';
import {type Document} from '@/matic/features/vault/documents/types/document';
import {type Revision} from '@/matic/features/vault/revisions/types';
import {type TrainingProgram} from '@/matic/features/training/programs/types/training-program';

// Union of resource types that can be passed to authorization checks.
// Each domain handler narrows to its expected type via `as` casts.
type AuthResource = Document | Revision | TrainingProgram;

// All valid "domain.permission" action strings, enforced at compile time.
type AuthAction =
  | `document.${'create' | 'edit' | 'delete' | 'view'}`
  | `revision.${'create' | 'publish' | 'download' | 'view_history' | 'edit' | 'revoke'}`
  | `admin.${'access_section' | 'access_master_data' | 'access_document_config' | 'access_users' | 'access_groups'}`
  | `training.${'access_programs' | 'create' | 'edit' | 'delete' | 'publish' | 'retire' | 'view'}`
  | `lattice.${'create' | 'modify' | 'delete' | 'run'}`;

function canDocumentPermission(
  user: AuthUser,
  permission: string,
  resource?: AuthResource
): boolean {
  switch (permission) {
    case 'create': {
      return documentPermissions.canCreateDocument(user);
    }

    case 'edit': {
      return documentPermissions.canEditDocument(user, resource as Document);
    }

    case 'delete': {
      return documentPermissions.canDeleteDocument(user, resource as Document);
    }

    case 'view': {
      return documentPermissions.canViewDocument(user, resource as Document);
    }

    default: {
      return false;
    }
  }
}

function canRevisionPermission(
  user: AuthUser,
  permission: string,
  resource?: AuthResource
): boolean {
  switch (permission) {
    case 'create': {
      return revisionPermissions.canCreateRevision(user, resource as Document);
    }

    case 'publish': {
      return revisionPermissions.canPublishRevision(user);
    }

    case 'download': {
      return revisionPermissions.canDownloadRevision(
        user,
        resource as Revision
      );
    }

    case 'view_history': {
      return revisionPermissions.canViewRevisionHistory(
        user,
        resource as Document
      );
    }

    // Note: edit and revoke require both revision and document — use useRevisionPermissions() instead
    case 'edit':
    case 'revoke': {
      console.warn(
        'useAuthorization.can("revision.edit|revoke") requires both revision and document. Use useRevisionPermissions() instead.'
      );
      return false;
    }

    default: {
      return false;
    }
  }
}

function canAdminPermission(user: AuthUser, permission: string): boolean {
  switch (permission) {
    case 'access_section':
    case 'access_master_data':
    case 'access_groups': {
      return user.role === 'admin' || user.role === 'quality';
    }

    case 'access_document_config':
    case 'access_users': {
      return user.role === 'admin';
    }

    default: {
      return false;
    }
  }
}

function canTrainingPermission(
  user: AuthUser,
  permission: string,
  resource?: AuthResource
): boolean {
  switch (permission) {
    case 'access_programs': {
      return trainingPermissions.canAccessTrainingPrograms(user);
    }

    case 'create': {
      return trainingPermissions.canCreateTrainingProgram(user);
    }

    case 'edit': {
      return trainingPermissions.canEditTrainingProgram(
        user,
        resource as TrainingProgram
      );
    }

    case 'delete': {
      return trainingPermissions.canDeleteTrainingProgram(
        user,
        resource as TrainingProgram
      );
    }

    case 'publish': {
      return trainingPermissions.canPublishTrainingProgram(user);
    }

    case 'retire': {
      return trainingPermissions.canRetireTrainingProgram(
        user,
        resource as TrainingProgram
      );
    }

    case 'view': {
      return trainingPermissions.canViewTrainingProgram(user);
    }

    default: {
      return false;
    }
  }
}

function canLatticePermission(user: AuthUser, permission: string): boolean {
  switch (permission) {
    case 'create': {
      return latticePermissions.canCreateLattice(user);
    }

    case 'modify': {
      return latticePermissions.canModifyLattice(user);
    }

    case 'delete': {
      return latticePermissions.canDeleteLattice(user);
    }

    case 'run': {
      return latticePermissions.canRunExtraction(user);
    }

    default: {
      return false;
    }
  }
}

/**
 * Unified authorization hook for checking user permissions.
 * Provides `can` and `check` methods to determine if the current user
 * has permission to perform a specific action on a given resource.
 *
 * Note on stability: `user` is sourced from `useAppContext()` which preserves
 * the last known good user via a ref during background OIDC token refreshes.
 * This keeps `can()` returning the correct answer (rather than `false`) while
 * `react-oidc-context` momentarily flips `isLoading`/`isAuthenticated` during
 * silent renew. `isLoading` and `isAuthenticated` are still passed through
 * from `useAuth` for callers that genuinely want the live auth state.
 */
export function useAuthorization() {
  const {user} = useAppContext();
  const {isAuthenticated, isLoading} = useAuth();

  /**
   * Checks if the current user has permission to perform an action.
   *
   * @param action A string in the format "domain.permission" (e.g., "document.create", "revision.edit").
   * @param resource Optional: The resource object (e.g., Document, Revision) the action is being performed on.
   * @returns true if the user has permission, false otherwise.
   */
  const can = (action: AuthAction, resource?: AuthResource): boolean => {
    if (!user) {
      return false;
    }

    const [domain, permission] = action.split('.');

    switch (domain) {
      case 'document': {
        return canDocumentPermission(user, permission, resource);
      }

      case 'revision': {
        return canRevisionPermission(user, permission, resource);
      }

      case 'admin': {
        return canAdminPermission(user, permission);
      }

      case 'training': {
        return canTrainingPermission(user, permission, resource);
      }

      case 'lattice': {
        return canLatticePermission(user, permission);
      }

      default: {
        return false;
      }
    }
  };

  /**
   * Guards an action by throwing an error if the current user does not have permission.
   * This is suitable for protecting mutations and imperative actions.
   *
   * @param action A string in the format "domain.permission".
   * @param resource Optional: The resource object.
   * @throws Error if the user does not have permission.
   */
  const check = (action: AuthAction, resource?: AuthResource): void => {
    if (!can(action, resource)) {
      throw new Error(
        `Authorization Error: You do not have permission to perform "${action}".`
      );
    }
  };

  return {user, isAuthenticated, isLoading, can, check};
}
